/**
 * Keeping a photograph's address the same while the one it has still works.
 *
 * <p><b>Every answer used to download every photograph on the screen again.</b> The server
 * answers each change with the whole audit, and it signs each file's link afresh on every
 * read — a new expiry, a new signature, a new address for the same bytes. An image whose
 * address changes is an image the browser fetches again, and a video is one whose first
 * frame it fetches again. Measured locally with three photographs on screen: three taps on
 * "Yes" made nine file downloads.</p>
 *
 * <p><b>The first version guessed how long a link works, and kept dead ones.</b> It held the
 * first address it saw for ten minutes from the moment it saw it. But the first address a
 * screen sees is the one in the device's cache, signed whenever the audit was last fetched —
 * often long ago. Reopening the app showed the cached link, the server's fresh one arrived a
 * second later and was thrown away, and the photograph stayed a broken-image icon that
 * could not be opened. Measured locally with links living twenty seconds: reopened after
 * twenty-five, the thumbnail was in its error state while a working link sat unused.</p>
 *
 * <p>So the server says when each link stops working, and a known address is reused only
 * while it has {@link Margin} left. One with no expiry — from a server or a cache older
 * than the field — is never kept. A phone whose clock is off can still keep one too long;
 * {@link forget} is how a failed load takes the newest address instead.</p>
 */
import type { AuditDetail } from '../api/audits';

/** How much life a kept link must have left: room for a slow load and a clock that is off. */
export const Margin = 2 * 60 * 1000;

export type LinkMemory = Map<string, { url: string; expiresAt: number }>;

/** The detail with each file's address replaced by the one already in use, while it works. */
export function steady(detail: AuditDetail, memory: LinkMemory, now: number): AuditDetail {
  return {
    ...detail,
    answers: detail.answers.map((answer) => ({
      ...answer,
      photos: answer.photos.map((photo) => {
        if (!photo.url) return photo;

        const known = memory.get(photo.id);
        if (known && known.expiresAt - now > Margin) return { ...photo, url: known.url };

        const expiresAt = photo.urlExpiresAt ? Date.parse(photo.urlExpiresAt) : NaN;

        if (Number.isFinite(expiresAt) && expiresAt - now > Margin) {
          memory.set(photo.id, { url: photo.url, expiresAt });
        } else {
          memory.delete(photo.id);
        }

        return photo;
      }),
    })),
  };
}

/** Drops what is remembered for a file whose address did not load, so the newest is used. */
export function forget(memory: LinkMemory, photoId: string): void {
  memory.delete(photoId);
}
